import React, {FC, useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {KTSVG} from '../../../../_metronic/helpers'
import {useAuth} from '../../auth'
import {Vessel} from '../../Management/core/_models'
import {useMonthEndFeatureAccess} from '../core/monthEndAccess'
import QhseAccessDenied from './QhseAccessDenied'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels } from '../../../shared/access/accessibleVessels'

const API_URL = process.env.REACT_APP_API_URL
const REPORT_API_URL = `${API_URL}/qhse/module1-reports`
type ReportOption = {id: number; name: string}

const getCurrentMonth = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  return `${year}-${month}`
}

const Module1ReportUploadModal: FC<{
  isOpen: boolean
  onClose: () => void
  selectedVesselId?: string
}> = ({isOpen, onClose, selectedVesselId}) => {
  const {auth, currentUser} = useAuth()
  const {canCreate, hasAssignedVessel} = useMonthEndFeatureAccess('module1-report')
  const isShipboardUser = hasAssignedVessel
  const userId = (auth?.userDetails as any)?.id
  const rankName = (currentUser as any)?.rank?.rank || (currentUser as any)?.rank?.name
  const roleName = (currentUser as any)?.role?.roleName || (currentUser as any)?.role?.name
  const defaultName = [(currentUser as any)?.firstName, (currentUser as any)?.lastName].filter(Boolean).join(' ').trim() || (auth?.userDetails as any)?.username
  const username = rankName || roleName || defaultName

  const [formData, setFormData] = useState({
    certificateNameId: '',
    certificateName: '',
    file: null as File | null,
    vesselId: '',
    remark: '',
  })
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [certificateNames, setCertificateNames] = useState<Array<{id: number; name: string}>>([])
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth())
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingVessels, setIsLoadingVessels] = useState(false)
  const [isLoadingLookups, setIsLoadingLookups] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!isOpen) return
    fetchVessels()
    fetchReportNames()
  }, [isOpen])

  useEffect(() => {
    if (isOpen && formData.vesselId) {
      fetchReportNames()
      setFormData((prev) => ({...prev, certificateNameId: ''}))
    }
  }, [formData.vesselId, isOpen])

  useEffect(() => {
    if (!isOpen) return
    if (!formData.vesselId && !selectedVesselId) return
    if (vessels.length === 0) return
    fetchReportNames()
  }, [vessels, isOpen, formData.vesselId, selectedVesselId])

  useEffect(() => {
    if (isOpen && isShipboardUser && currentUser?.vessel?.id) {
      setFormData((prev) => ({...prev, vesselId: currentUser.vessel!.id.toString()}))
    } else if (isOpen && isShipboardUser) {
      toast.error('No vessel assigned to this crew member')
      setFormData((prev) => ({...prev, vesselId: ''}))
    }
  }, [isOpen, isShipboardUser, currentUser])

  const resolveCompanyGroupIdForLookup = (): string | null => {
    const targetVesselId = formData.vesselId || selectedVesselId || currentUser?.vessel?.id?.toString()
    if (targetVesselId) {
      const selectedVessel = vessels.find((v) => String(v.id) === String(targetVesselId))
      const vesselCompanyGroupId =
        (selectedVessel as any)?.companyGroupAdmin?.id ??
        (selectedVessel as any)?.companyAdmin?.cga?.id
      if (vesselCompanyGroupId != null) {
        return String(vesselCompanyGroupId)
      }
    }

    const currentUserCompanyGroupId =
      (currentUser as any)?.companyGroupAdminId ?? (currentUser as any)?.companyGroupAdmin?.id
    if (currentUserCompanyGroupId != null) {
      return String(currentUserCompanyGroupId)
    }

    return null
  }

  const fetchReportNames = async () => {
    setIsLoadingLookups(true)
    try {
      const token = auth?.auth.jwt
      const params = new URLSearchParams()
      params.append('activeOnly', 'true')
      params.append('page', '0')
      params.append('size', '1000')
      const companyGroupId = resolveCompanyGroupIdForLookup()
      if (!companyGroupId) {
        setCertificateNames([])
        return
      }
      params.append('companyGroupId', companyGroupId)

      const res = await fetch(`${API_URL}/qhse/module1-report-names/library?${params.toString()}`, {
        headers: {Authorization: `Bearer ${token}`},
      })
      if (!res.ok) throw new Error('Failed to load report names')
      const payload = await res.json()
      const content = Array.isArray(payload) ? payload : payload.content || []
      const filteredByCompany = content.filter((n: any) => {
        if (!companyGroupId) return false
        if (n?.companyGroupAdminId == null) return false
        return String(n.companyGroupAdminId) === companyGroupId
      })
      const mapped: ReportOption[] = filteredByCompany.map((n: any) => ({
        id: Number(n.id),
        name: String(n.name ?? ''),
      }))
      setCertificateNames(mapped)
      setFormData((prev) => {
        if (!prev.certificateNameId) return prev
        const stillAllowed = mapped.some((item: ReportOption) => String(item.id) === prev.certificateNameId)
        return stillAllowed ? prev : {...prev, certificateNameId: ''}
      })
    } catch (error: any) {
      console.error('Failed to fetch report names:', error)
      toast.error('Failed to load report options')
    } finally {
      setIsLoadingLookups(false)
    }
  }

  const fetchVessels = async () => {
    if (isShipboardUser) {
      setVessels([])
      setIsLoadingVessels(false)
      return
    }

    setIsLoadingVessels(true)
    try {
      const vesselList = await fetchAccessibleVesselsForCurrentUser()
      setVessels(normalizeAccessibleVessels(vesselList))
    } catch (error) {
      console.error('Failed to fetch vessel list:', error)
      toast.error('Failed to load vessels')
    } finally {
      setIsLoadingVessels(false)
    }
  }

  const resetForm = () => {
    setFormData({certificateNameId: '', certificateName: '', file: null, vesselId: '', remark: ''})
    setSelectedMonth(getCurrentMonth())
    setErrors({})
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    if (!formData.certificateNameId) newErrors.certificateNameId = 'Report Name is required'
    if (!isShipboardUser && !formData.vesselId) newErrors.vesselId = 'Please select a vessel'
    if (!formData.file) {
      newErrors.file = 'PDF file is required'
    } else if (formData.file.type !== 'application/pdf') {
      newErrors.file = 'Only PDF files are allowed'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return
    setIsSubmitting(true)

    try {
      const token = auth?.auth.jwt
      const form = new FormData()
      form.append('reportName', formData.certificateName)
      form.append('uploadedBy', String(userId ?? ''))
      form.append('uploadedByName', username ?? '')
      form.append('vesselId', formData.vesselId)
      if (selectedMonth) form.append('dateOfIssue', `${selectedMonth}-01`)
      form.append('remarks', formData.remark)
      form.append('file', formData.file!)

      const response = await fetch(`${REPORT_API_URL}/upload`, {
        method: 'POST',
        headers: {Authorization: `Bearer ${token}`},
        body: form,
      })

      if (!response.ok) throw new Error('Upload failed')
      toast.success('Report uploaded successfully!')
      resetForm()
      onClose()
    } catch (error) {
      console.log(error)
      toast.error('Failed to upload report')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null
  if (!canCreate) {
    return (
      <div className='modal fade show d-block' tabIndex={-1} style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
        <div className='modal-dialog modal-dialog-centered'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>
                <KTSVG path='/media/icons/duotune/files/fil003.svg' className='svg-icon-3 me-3' />
                Upload Report
              </h5>
              <button type='button' className='btn-close' onClick={() => {resetForm(); onClose()}} disabled={isSubmitting} />
            </div>
            <div className='modal-body'>
              <QhseAccessDenied message='Access denied — you do not have permission to upload Month End Reports.' />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='modal fade show d-block' tabIndex={-1} style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
      <div className='modal-dialog modal-dialog-centered'>
        <div className='modal-content'>
          <form onSubmit={handleSubmit}>
            <div className='modal-header'>
              <h5 className='modal-title'>
                <KTSVG path='/media/icons/duotune/files/fil003.svg' className='svg-icon-3 me-3' />
                Upload Report
              </h5>
              <button type='button' className='btn-close' onClick={() => {resetForm(); onClose()}} disabled={isSubmitting} />
            </div>

            <div className='modal-body'>
              {!isShipboardUser && (
                <div className='mb-6'>
                  <label className='required fw-semibold fs-6 mb-2'>Select Vessel</label>
                  <SearchableSelect
                    className={`form-select form-select-solid ${errors.vesselId ? 'is-invalid' : ''}`}
                    value={formData.vesselId}
                    onChange={(e) => setFormData((prev) => ({...prev, vesselId: e.target.value}))}
                    disabled={isSubmitting || isLoadingVessels}
                  >
                    <option value=''>{isLoadingVessels ? 'Loading vessels...' : 'Choose a vessel'}</option>
                    {vessels.map((vessel) => (
                      <option key={vessel.id} value={vessel.id}>
                        {(vessel as any).fleet_name || vessel.name}
                      </option>
                    ))}
                  </SearchableSelect>
                  {errors.vesselId && <div className='invalid-feedback'>{errors.vesselId}</div>}
                </div>
              )}

              <div className='mb-6'>
                <label className='required fw-semibold fs-6 mb-2'>Report Name</label>
                <SearchableSelect
                  className={`form-select form-select-solid ${errors.certificateNameId ? 'is-invalid' : ''}`}
                  value={formData.certificateNameId}
                  onChange={(e) => {
                    const selectedId = e.target.value
                    const selectedItem = certificateNames.find((item) => String(item.id) === selectedId)
                    setFormData((prev) => ({
                      ...prev,
                      certificateNameId: selectedId,
                      certificateName: selectedItem?.name || '',
                    }))
                  }}
                  disabled={isSubmitting || isLoadingLookups}
                >
                  <option value=''>{isLoadingLookups ? 'Loading report names...' : 'Select Report Name'}</option>
                  {certificateNames.map((item) => (
                    <option key={item.id} value={item.id}>{item.name}</option>
                  ))}
                </SearchableSelect>
                {errors.certificateNameId && <div className='invalid-feedback'>{errors.certificateNameId}</div>}
              </div>

              <div className='mb-6'>
                <label className='fw-semibold fs-6 mb-2'>Remark</label>
                <textarea
                  className='form-control form-control-solid'
                  placeholder='Enter any remarks or notes (optional)'
                  rows={3}
                  value={formData.remark}
                  onChange={(e) => setFormData((prev) => ({...prev, remark: e.target.value}))}
                  disabled={isSubmitting}
                />
              </div>

              <div className='mb-6'>
                <label className='fw-semibold fs-6 mb-2'>Month</label>
                <input
                  type='month'
                  className='form-control form-control-solid'
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>

              <div className='mb-6'>
                <label className='required fw-semibold fs-6 mb-2'>Report File (PDF only)</label>
                <input
                  type='file'
                  className={`form-control form-control-solid ${errors.file ? 'is-invalid' : ''}`}
                  accept='.pdf'
                  onChange={(e) => setFormData((prev) => ({...prev, file: e.target.files?.[0] || null}))}
                  disabled={isSubmitting}
                />
                {errors.file && <div className='invalid-feedback'>{errors.file}</div>}
              </div>
            </div>

            <div className='modal-footer'>
              <button type='button' className='btn btn-light' onClick={() => {resetForm(); onClose()}} disabled={isSubmitting}>
                Cancel
              </button>
              <button type='submit' className='btn btn-primary' disabled={isSubmitting}>
                {isSubmitting ? 'Uploading...' : <><KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-4 me-2' />Upload Report</>}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default Module1ReportUploadModal
